import { Fragment, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { NavCaret, NavIcon } from './navIcons';
import { ADMIN_PORTAL_NAV, isNavParent, navLeafOwnsPath, type NavEntry, type NavLeaf } from './nav';

function findOpenParentId(pathname: string): string | null {
  for (const section of ADMIN_PORTAL_NAV) {
    for (const item of section.items) {
      if (isNavParent(item) && item.children.some((child) => navLeafOwnsPath(child, pathname))) {
        return item.id;
      }
    }
  }
  return null;
}

const QUESTIONS_PATH = '/people/employee-questions';

/** A top-level row keeps its icon; a child of an expanded group has none (its connecting line and dot are drawn by CSS on the `.nav-child` row, so
 * they are decorative and never focusable). The active route is marked with `aria-current="page"`. */
function LeafLink({ leaf, count = 0, child = false }: { leaf: NavLeaf; count?: number; child?: boolean }) {
  const { pathname } = useLocation();
  const isActive = navLeafOwnsPath(leaf, pathname);
  return (
    <Link
      to={leaf.path}
      className={isActive ? 'nav-link nav-link--active' : 'nav-link'}
      aria-current={isActive ? 'page' : undefined}
      title={leaf.help}
      data-nav-id={leaf.id}
    >
      {!child && <NavIcon name={leaf.icon} filled={isActive} />}
      {leaf.label}
      {count > 0 && <span className="nav-count" aria-label={`${count} open`}>{count > 99 ? '99+' : count}</span>}
      {leaf.badgeLevel && (
        <span className={`badge badge--${leaf.badgeLevel}`}> {leaf.badgeLevel === 'blocked' ? 'Blocked' : 'Limited'}</span>
      )}
    </Link>
  );
}

/** Consecutive rows that share a `subgroup` label are rendered together under that label; rows with none stay in one plain list. */
function bySubgroup(items: NavEntry[]): { subgroup: string | undefined; items: NavEntry[] }[] {
  const runs: { subgroup: string | undefined; items: NavEntry[] }[] = [];
  for (const item of items) {
    const last = runs[runs.length - 1];
    if (last && last.subgroup === item.subgroup) last.items.push(item);
    else runs.push({ subgroup: item.subgroup, items: [item] });
  }
  return runs;
}

/**
 * Single-expand accordion sidebar. Exactly one NavParent is open at a time; opening a different one closes whichever was open. The open parent is
 * derived from the current route on load/navigation (so a deep link or refresh lands with the right section already expanded, including a nested route
 * like `/payroll-processing/:runId/:step`) but tracked as local state afterward, so manually toggling a parent closed isn't immediately overridden
 * until the route actually changes again. Everything shown comes from `nav.ts`; the sidebar never decides by permission or plan whether a row shows
 * (the server answers for the page), and it renders no empty header or subgroup.
 */
export function SideNav({ openQuestionCount = 0 }: { openQuestionCount?: number } = {}) {
  const location = useLocation();
  const openQuestions = openQuestionCount;
  const [openId, setOpenId] = useState<string | null>(() => findOpenParentId(location.pathname));

  useEffect(() => {
    // Every row has its own path, so the parent matching the new destination is unambiguous: open it, or resolve to null when the destination belongs
    // to no accordion parent at all (a plain row such as Overview) rather than leaving an unrelated previously-open parent expanded. This effect only
    // re-runs when the route actually changes, so manually toggling the open parent closed via its own button still works.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOpenId(findOpenParentId(location.pathname));
  }, [location.pathname]);

  /** The waiting-question count belongs to the Employee Questions row, wherever it sits. */
  const countFor = (leaf: NavLeaf) => (leaf.path === QUESTIONS_PATH ? openQuestions : 0);

  const renderEntry = (item: NavEntry) => {
    if (!isNavParent(item)) {
      return <li key={item.id}><LeafLink leaf={item} count={countFor(item)} /></li>;
    }

    const isOpen = openId === item.id;
    const panelId = `nav-panel-${item.id.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
    return (
      <li key={item.id} className={isOpen ? 'nav-parent nav-parent--open' : 'nav-parent'}>
        <button
          type="button"
          className={`nav-link nav-parent-toggle${isOpen ? ' nav-parent-toggle--open' : ''}`}
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={() => setOpenId(isOpen ? null : item.id)}
        >
          <NavIcon name={item.icon} filled={isOpen} />
          <span className="nav-parent-label">{item.label}</span>
          <NavCaret />
        </button>
        {/* Always rendered, never conditionally removed, so aria-controls always points to a real, existing, unique element and a screen reader's
            virtual cursor can still reach the panel's own DOM node. `hidden` (not a CSS display toggle on a wrapper) is what actually removes it from
            layout and the accessibility tree while closed. */}
        <ul id={panelId} className="nav-children" hidden={!isOpen}>
          {item.children.map((child) => (
            <li key={child.id} className="nav-child"><LeafLink leaf={child} child count={countFor(child)} /></li>
          ))}
        </ul>
      </li>
    );
  };

  return (
    <>
      {ADMIN_PORTAL_NAV.map((section) => (
        <div key={section.id}>
          <p className="nav-section-label">{section.label}</p>
          <ul>
            {bySubgroup(section.items).map((run, index) => (
              run.subgroup === undefined ? (
                <Fragment key={`plain-${index}`}>{run.items.map(renderEntry)}</Fragment>
              ) : (
                <li key={run.subgroup} className="nav-subgroup">
                  <p className="nav-subgroup-label" id={`nav-subgroup-${section.id}-${run.subgroup.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{run.subgroup}</p>
                  <ul aria-labelledby={`nav-subgroup-${section.id}-${run.subgroup.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    {run.items.map(renderEntry)}
                  </ul>
                </li>
              )
            ))}
          </ul>
        </div>
      ))}
    </>
  );
}
